import React, { useEffect, useState } from 'react';
import { useTutorial } from '../../contexts/TutorialContext';
import { MobileTutorialTextBox } from './MobileTutorialTextBox';
import {
  MOBILE_TUTORIAL_STEPS,
  TOTAL_MOBILE_TUTORIAL_STEPS,
} from './mobileTutorialContent';

export const MobileTutorialOverlay: React.FC = () => {
  const { tutorialState, nextPage, skipTutorial } = useTutorial();
  const [isScrolling, setIsScrolling] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);

  const currentStep = MOBILE_TUTORIAL_STEPS.find(
    (step) => step.id === tutorialState.currentPage
  );

  // Auto-scroll to target element when step changes
  useEffect(() => {
    if (!currentStep || !currentStep.targetElementId) {
      setHasScrolled(true);
      return;
    }

    const scrollToElement = async () => {
      setIsScrolling(true);
      setHasScrolled(false);

      // Small delay to ensure DOM is ready
      await new Promise((resolve) => setTimeout(resolve, 100));

      const targetElement = document.querySelector(
        `[data-tutorial-id="${currentStep.targetElementId}"]`
      );

      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });

        // Wait for scroll to complete
        setTimeout(() => {
          setIsScrolling(false);
          setHasScrolled(true);
        }, 600); // Smooth scroll typically takes ~500ms
      } else {
        console.warn(
          `Tutorial: Element with data-tutorial-id="${currentStep.targetElementId}" not found`
        );
        setIsScrolling(false);
        setHasScrolled(true);
      }
    };

    scrollToElement();
  }, [currentStep]);

  // Handle tap to advance
  const handleTapToAdvance = (e: React.MouseEvent) => {
    // Don't advance while scrolling or before scroll completes
    if (isScrolling || !hasScrolled) {
      return;
    }

    // On last step, complete instead of advance
    if (currentStep?.isLast) {
      skipTutorial(); // This marks as completed
      return;
    }

    nextPage();
  };

  const handleSkip = () => {
    skipTutorial();
  };

  if (!tutorialState.isActive || !currentStep) {
    return null;
  }

  return (
    <>
      {/* Tap overlay - covers entire screen */}
      <div
        className="fixed inset-0 z-[9999] bg-transparent cursor-pointer"
        onClick={handleTapToAdvance}
        aria-label="Tap to continue tutorial"
      />

      {/* Text box at bottom */}
      <MobileTutorialTextBox
        step={currentStep}
        totalSteps={TOTAL_MOBILE_TUTORIAL_STEPS}
        onSkip={handleSkip}
        onAdvance={handleTapToAdvance}
      />
    </>
  );
};
